'use client';

import { useState, useCallback, useEffect } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { TerminalComponent } from '@/components/Terminal';
import { useAuth } from '@/lib/auth-context';

type SessionStatus = 'loading' | 'running' | 'completed' | 'error';

interface Decision {
  id: string;
  title: string;
  description: string;
  reasoning: string;
  category: string;
  importance: string;
}

function getWebSocketUrl(): string {
  if (typeof window === 'undefined') return 'ws://localhost:3001';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/terminal`;
}

export default function SuggestTodoPage() {
  const params = useParams();
  const decisionId = params?.id as string | undefined;
  const { permissions, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [exitCode, setExitCode] = useState<number | null>(null);
  const [wsUrl, setWsUrl] = useState<string>('');
  const [decision, setDecision] = useState<Decision | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Set WebSocket URL on client side only
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWsUrl(getWebSocketUrl());
    }
  }, []);

  // Fetch decision data
  useEffect(() => {
    if (!decisionId) return;

    fetch(`/api/decisions/${decisionId}`)
      .then(res => {
        if (!res.ok) throw new Error('Decision not found');
        return res.json();
      })
      .then(data => {
        setDecision(data.decision);
        setStatus('running');
      })
      .catch(err => {
        setError(err.message);
        setStatus('error');
      });
  }, [decisionId]);

  const handleExit = useCallback((code: number | null) => {
    setExitCode(code);
    setStatus(code === 0 ? 'completed' : 'error');
  }, []);

  const handleError = useCallback((errorMsg: string) => {
    console.error('Terminal error:', errorMsg);
    setStatus('error');
  }, []);

  const handleRestart = useCallback(() => {
    setStatus('running');
    setExitCode(null);
  }, []);

  // Show loading state
  if (authLoading || status === 'loading') {
    return (
      <div className="flex flex-col h-[calc(100vh-113px)] max-w-6xl mx-auto px-4 pt-4 pb-2">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-zinc-500">Loading...</div>
        </div>
      </div>
    );
  }

  // Check for reflect permission (admin only)
  if (!permissions.canReflect) {
    return (
      <div className="flex flex-col h-[calc(100vh-113px)] max-w-6xl mx-auto px-4 pt-4 pb-2">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Access Denied
            </h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Only administrators can suggest todos.
            </p>
            <Link
              href={`/decisions/${decisionId}`}
              className="inline-block px-4 py-2 bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg transition-colors text-sm"
            >
              Back to Decision
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show error if decision not found
  if (error) {
    return (
      <div className="flex flex-col h-[calc(100vh-113px)] max-w-6xl mx-auto px-4 pt-4 pb-2">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Decision Not Found
            </h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              {error}
            </p>
            <Link
              href="/decisions"
              className="inline-block px-4 py-2 bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg transition-colors text-sm"
            >
              Back to Decisions
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-113px)] max-w-6xl mx-auto px-4 pt-4 pb-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Link
            href={`/decisions/${decisionId}`}
            className="inline-flex items-center text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            ← Back
          </Link>
          <div>
            <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
              Suggest Follow-up Todo
            </h1>
            {decision && (
              <p className="text-sm text-zinc-500 dark:text-zinc-400 truncate max-w-md">
                {decision.title}
              </p>
            )}
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex items-center gap-2">
          <span
            className={`w-2 h-2 rounded-full ${
              status === 'running'
                ? 'bg-green-500 animate-pulse'
                : status === 'completed'
                ? 'bg-blue-500'
                : 'bg-red-500'
            }`}
          />
          <span className="text-sm text-zinc-600 dark:text-zinc-400 capitalize">
            {status}
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Terminal container */}
        <div className="flex-1 bg-[#fafafa] dark:bg-[#1a1b26] rounded-lg overflow-hidden border border-zinc-300 dark:border-zinc-700">
          {wsUrl && decision && decisionId && (
            <TerminalComponent
              wsUrl={wsUrl}
              command={`/suggest-todo ${decisionId}`}
              onExit={handleExit}
              onError={handleError}
            />
          )}
          {!decisionId && (
            <div className="flex items-center justify-center h-full text-red-500">
              Error: No decision ID in URL
            </div>
          )}
        </div>

        {/* Footer actions */}
        {(status === 'completed' || status === 'error') && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-zinc-600 dark:text-zinc-400">
              {status === 'completed' ? (
                <span className="text-green-600 dark:text-green-400">
                  Suggestion session completed
                </span>
              ) : (
                <span className="text-red-600 dark:text-red-400">
                  Session ended with exit code {exitCode}
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <Link
                href={`/decisions/${decisionId}`}
                className="px-4 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
              >
                Back to Decision
              </Link>
              <button
                onClick={handleRestart}
                className="px-4 py-2 text-sm bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg transition-colors"
              >
                Restart Session
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
